import {
  ENVIRONMENT_NAME,
  GA_MEASUREMENT_ID,
  GTAG_URL,
  SENTRY_DSN
} from "config/config";
import * as Sentry from "@sentry/react";
import type { Breadcrumb, ErrorEvent } from "@sentry/react";
import {
  getLoggedinUserId,
  getSubscriptionId,
  getProjectId
} from "services/auth";
import { SentryErrorSources, SentryTags } from "../constants";
import { isLocalhostDevEnv } from "./utils";

function initGA() {
  if (GTAG_URL && GA_MEASUREMENT_ID && !isLocalhostDevEnv()) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `${GTAG_URL}?id=${GA_MEASUREMENT_ID}`;
    document.body.appendChild(script);
    const windowRef: any = window as any;
    windowRef.dataLayer = windowRef.dataLayer || [];
    windowRef.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      windowRef.dataLayer.push(arguments);
    };
    windowRef.gtag("js", new Date());
    // gtag("js") only stamps the load time; a measurement id collects nothing
    // until it is configured. Do it here so traffic is captured from load
    // rather than from the first authenticated render.
    windowRef.gtag("config", GA_MEASUREMENT_ID);
  }
}

// "set" rather than a second "config": config re-sends a page_view, and this
// runs on every PrivateRoutes render.
function setGAUserId() {
  const windowRef: any = window as any;
  const loggedInUserId: string = getLoggedinUserId();
  if (typeof windowRef.gtag === "function" && loggedInUserId) {
    windowRef.gtag("set", { user_id: loggedInUserId });
  }
}

// Sources whose errors are handled backend failures (API / GraphQL / auth) and
// therefore don't warrant a session replay. Keep in sync with the error_source
// tags set by error-manager.handleError.
const REPLAY_SKIP_SOURCES: ReadonlyArray<string> = [
  SentryErrorSources.api,
  SentryErrorSources.graphql,
  SentryErrorSources.auth
];

// All error_source values we expect to be explicitly set somewhere. An error
// whose source is none of these is a tagging gap (see beforeSend observability).
const KNOWN_ERROR_SOURCES: ReadonlyArray<string> = [
  SentryErrorSources.api,
  SentryErrorSources.graphql,
  SentryErrorSources.auth,
  SentryErrorSources.frontend
];

function isKnownErrorSource(source: unknown): boolean {
  return typeof source === "string" && KNOWN_ERROR_SOURCES.includes(source);
}

// A replay is worth capturing only when the error is NOT a handled backend
// failure — i.e. it likely reflects a broken UI.
function isReplayWorthyError(event: ErrorEvent): boolean {
  const source = event.tags?.[SentryTags.error_source];
  return !(typeof source === "string" && REPLAY_SKIP_SOURCES.includes(source));
}

// `allowUrls` (below, in Sentry.init) drops exceptions whose stack frames are
// all outside our own origin, but it has nothing to match when there's no
// stack at all — e.g. a non-Error object rejected across a promise boundary
// (third-party async loaders commonly do this). Catch that remaining gap
// here: an exception event with zero attributable frames is not actionable
// either way, so drop it too. Message-only events (Sentry.captureMessage)
// have no `exception` payload and are unaffected.
function hasNoAttributableStack(event: ErrorEvent): boolean {
  if (!event.exception) {
    return false;
  }
  const frames = event.exception.values?.flatMap(
    (value) => value.stacktrace?.frames || []
  );
  return !frames || frames.length === 0;
}

/**
 * Setup the Sentry tool for our website
 * Features:
 * - Error catching
 * - Browser performance matrix
 * - Replay functionality
 */
function initSentry() {
  if (SENTRY_DSN && !isLocalhostDevEnv()) {
    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Additional SDK configuration goes in here, for example:
          maskAllText: true,
          blockAllMedia: true,
          // Only spend a session replay when the UI actually breaks. Handled
          // backend failures — API / GraphQL / auth (token, 401/403) errors —
          // are reported through error-manager.handleError or the
          // session-manager, which tag them with error_source = "api" |
          // "graphql" | "auth". Skip the replay for those so we don't burn
          // replay quota (or record a session) for expected request/auth
          // failures. Anything else (uncaught render crashes, ErrorBoundary,
          // etc.) keeps the global "frontend" source and still captures a
          // replay.
          beforeErrorSampling: (event: ErrorEvent): boolean =>
            isReplayWorthyError(event)
        })
      ],
      tracesSampleRate: 1.0,
      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 0,

      // Compounds with beforeErrorSampling above: that decides which errors are
      // replay-worthy, this decides how many of those actually spend quota.
      // Buffer mode already caps us at one replay per session, so the remaining
      // burn is the same issue recurring across sessions and users — sampling is
      // the blunt-but-predictable lever for that. Tune from the
      // ciq.replay_eligible tag once there's a week of production data; prefer
      // this over a Sentry rate limit, which would truncate exactly the
      // bad-deploy burst we most need to see.
      replaysOnErrorSampleRate: 0.25,

      environment: ENVIRONMENT_NAME || "Dummy",
      ignoreErrors: [
        "ResizeObserver loop completed with undelivered notifications",
        // Browser-extension / injected-script rejections: none of these
        // strings exist anywhere in our bundle. Message-matching is the only
        // reliable filter — the offending scripts run in page context, so
        // their rejections carry no frame URLs for allowUrls to act on.
        /unknown action: (set|get|remove)LibItems?/,
        "cannot communicate with background",
        "Non-Error promise rejection captured with value: timeout",
        "Non-Error promise rejection captured with value: undefined",
        // WalkMe user-mapping loader timeout (injected, not in our CSP)
        /keys: data, message, userMapping/,
        // WalkMe jQuery events rejected as promises — its bundled jQuery
        // stamps a wmjQuery<digits> marker key on every event object
        /wmjQuery/,
        /\(type=visibilitychange\) captured as promise rejection/
      ],
      // Matches any of our subdomains (app./dev./perf.constructiviq.com,
      // etc.) via a loose, unanchored match rather than the exact runtime
      // origin. Deliberately not tightened to an anchored/protocol-bound
      // pattern: a stack frame referencing a lookalike domain (e.g.
      // constructiviq.com used deceptively as part of another host) should
      // still fail to match and therefore still be reported, not silently
      // dropped, since that's a signal worth seeing rather than filtering.
      allowUrls: [/constructiviq\.com/],
      beforeSend: (event: ErrorEvent): ErrorEvent | null => {
        if (hasNoAttributableStack(event)) {
          return null;
        }

        const breadcrumbs: Breadcrumb[] = event.breadcrumbs || [];

        const clonedEvent: ErrorEvent = { ...event };
        clonedEvent.breadcrumbs = breadcrumbs.filter(
          (b: Breadcrumb) => !b.data?.url?.includes("op=AuditChangeEvents")
        );
        // Replay-filter observability. Stamp every error so we can validate the
        // optimization in Sentry without extra telemetry infra:
        //   CIQ.replay_eligible      — did this error qualify for a replay?
        //   CIQ.error_source_tagged  — was error_source explicitly set?
        // Query `CIQ.replay_eligible:true AND CIQ.error_source_tagged:false` to
        // find call sites that omit the source tag and silently consume replay
        // quota. Tag keys are case-sensitive in Sentry search.
        const source = event.tags?.[SentryTags.error_source];
        clonedEvent.tags = {
          ...clonedEvent.tags,
          [SentryTags.replay_eligible]: isReplayWorthyError(event),
          [SentryTags.error_source_tagged]: isKnownErrorSource(source)
        };
        return clonedEvent;
      }
    });
    Sentry.setTag(SentryTags.error_source, SentryErrorSources.frontend);
  }
}

// subscription_id + project_id are Hasura UUIDs (no PII). Attaching them lets
// GA4 slice active users and engagement time by subscription and project.
// A blank value is a real dimension value to GA4, so an unset id would surface
// as an empty row in every breakdown. Omit the key instead.
function gaUserContext(): Record<string, string> {
  const context: Record<string, string> = {};
  const subscriptionId = getSubscriptionId();
  const projectId = getProjectId();
  if (subscriptionId) {
    context.subscription_id = subscriptionId;
  }
  if (projectId) {
    context.project_id = projectId;
  }
  return context;
}

// Generic GA4 custom-event emitter. Dev/localhost is excluded (matches initGA)
// and a missing gtag (script blocked / not yet loaded) is a silent no-op.
function trackEvent(name: string, params?: Record<string, unknown>) {
  if (isLocalhostDevEnv()) {
    return;
  }
  const windowRef: any = globalThis.window as any;
  if (typeof windowRef.gtag === "function") {
    windowRef.gtag("event", name, params);
  }
}

// Snapshot of concurrent tab count for one user, tagged with subscription and
// project so multi-tab usage slices the same way as engagement.
function trackTabHeartbeat(tabCount: number) {
  if (isLocalhostDevEnv()) {
    return;
  }
  trackEvent("tab_heartbeat", { tab_count: tabCount, ...gaUserContext() });
  Sentry.setTag(SentryTags.tab_count, String(tabCount));
}

// Clear the tab_count tag so a stale count from this tab doesn't mislabel later
// Sentry events after it stops heartbeating (logout / unmount).
function clearTabHeartbeatTag() {
  Sentry.setTag(SentryTags.tab_count, undefined);
}

// Registers subscription_id + project_id as GA4 user properties so every event
// (including GA4's automatic engagement metrics) is sliceable by them.
function updateGAUserProperties() {
  const windowRef: any = globalThis.window as any;
  const context = gaUserContext();
  if (typeof windowRef.gtag === "function" && Object.keys(context).length > 0) {
    windowRef.gtag("set", "user_properties", context);
  }
}

export {
  initGA,
  setGAUserId,
  updateGAUserProperties,
  initSentry,
  trackEvent,
  trackTabHeartbeat,
  clearTabHeartbeatTag
};
